/**
 * UUID Version 1 Generator
 *
 * Generates time-based UUIDs according to RFC 9562 (previously RFC 4122).
 * Format: xxxxxxxx-xxxx-1xxx-yxxx-xxxxxxxxxxxx
 *
 * Structure:
 * - 32 bits: time_low (low field of timestamp)
 * - 16 bits: time_mid (middle field of timestamp)
 * - 4 bits: Version (0001 = 1)
 * - 12 bits: time_hi (high field of timestamp)
 * - 2 bits: Variant (10)
 * - 14 bits: clock_seq (clock sequence)
 * - 48 bits: node (typically MAC address, using random here)
 *
 * The timestamp is a 60-bit value representing 100-nanosecond intervals
 * since October 15, 1582 00:00:00 UTC (the UUID epoch).
 *
 * @see https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-1
 */

// UUID v1 epoch: October 15, 1582 00:00:00 UTC
// Offset from Unix epoch in 100-nanosecond intervals
const UUID_V1_EPOCH_OFFSET = 122192928000000000n;

// Track last timestamp and clock sequence for monotonicity
let lastTimestamp = 0n;
let clockSeq = Math.floor(Math.random() * 0x3fff); // 14-bit random clock sequence

/**
 * Generates a new UUID Version 1
 */
export function generateUUIDv1(): string {
  // Get current timestamp in milliseconds
  const now = Date.now();

  // Convert to 100-nanosecond intervals since UUID epoch
  // JavaScript's Date.now() gives milliseconds, so we multiply by 10000 to get 100-nanosecond intervals
  let timestamp = BigInt(now) * 10000n + UUID_V1_EPOCH_OFFSET;

  // Ensure timestamp is monotonically increasing
  if (timestamp <= lastTimestamp) {
    timestamp = lastTimestamp + 1n;
    // Increment clock sequence if we're generating multiple UUIDs in the same time period
    clockSeq = (clockSeq + 1) & 0x3fff;
  }
  lastTimestamp = timestamp;

  // Extract timestamp components (60 bits total)
  // time_low: bits 0-31 (32 bits)
  const timeLow = Number(timestamp & 0xffffffffn);
  // time_mid: bits 32-47 (16 bits)
  const timeMid = Number((timestamp >> 32n) & 0xffffn);
  // time_hi: bits 48-59 (12 bits)
  const timeHi = Number((timestamp >> 48n) & 0xfffn);

  // Format timestamp components as hex strings
  const timeLowHex = timeLow.toString(16).padStart(8, '0');
  const timeMidHex = timeMid.toString(16).padStart(4, '0');

  // Combine time_hi with version (version 1 = 0001)
  const timeHiAndVersion = (timeHi | 0x1000).toString(16).padStart(4, '0');

  // Generate variant and clock sequence
  // Variant 10xx (RFC 9562), so we set top 2 bits to 10
  const clockSeqHiAndVariant = ((clockSeq >> 8) | 0x80).toString(16).padStart(2, '0');
  const clockSeqLow = (clockSeq & 0xff).toString(16).padStart(2, '0');
  const clockSeqHex = clockSeqHiAndVariant + clockSeqLow;

  // Generate random node (48 bits)
  // In real UUID v1, this would be the MAC address
  // We use random values with multicast bit set (bit 0 of first octet)
  const nodeBytes = new Uint8Array(6);
  crypto.getRandomValues(nodeBytes);
  // Set multicast bit to indicate this is not a real MAC address
  nodeBytes[0] |= 0x01;
  const node = Array.from(nodeBytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');

  // Construct UUID in standard format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  return `${timeLowHex}-${timeMidHex}-${timeHiAndVersion}-${clockSeqHex}-${node}`;
}
